import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { auth } from '../utils/firebase-config';

const TWOFA_URL     = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/twofa';
const PROVISION_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/provision-trial-public';

export default function TwoFactorSetup({ user, gido, onComplete }) {
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { enroll(); }, []);

  const enroll = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(TWOFA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'enroll' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Enrollment failed');
      setSecret(data.secret);
      setQrCodeUrl(data.otpauth_uri);
    } catch (err) {
      console.error('2FA enroll error:', err);
      setError(err.message);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(TWOFA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'confirm', code: verificationCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code. Please try again.');

      // Provision SmartData trial automatically
      try {
        const t = await auth.currentUser.getIdToken();
        await fetch(PROVISION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: t }),
        });
      } catch (provisionErr) {
        console.warn('Provisioning failed silently:', provisionErr);
      }

      onComplete();
    } catch (err) {
      console.error('2FA setup error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="twofa-setup-container">
      <div className="twofa-card">
        <h2>🔐 Set Up Two-Factor Authentication</h2>
        <p className="twofa-subtitle">Almost done — secure your account. Scan this QR code with your authenticator app (e.g. Google Authenticator). After this, sign in at the portal to finish setup.</p>

        {qrCodeUrl && (
          <div className="qr-code-section">
            <QRCodeSVG value={qrCodeUrl} size={200} level="M" includeMargin={true} />
            <div className="manual-entry">
              <p className="manual-label">Can't scan? Enter manually:</p>
              <code className="secret-code">{secret}</code>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleVerify} className="verification-form">
          <div className="input-group">
            <label htmlFor="code">Enter 6-Digit Code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength="6"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              required
              disabled={loading}
              autoFocus
              className="code-input"
            />
          </div>
          <button type="submit" disabled={loading || verificationCode.length !== 6} className="verify-button">
            {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
          </button>
        </form>

        <div className="twofa-help">
          <p><strong>Don't have Google Authenticator?</strong><br/>
          Download it from your app store and add this account.</p>
        </div>
      </div>
    </div>
  );
}